import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { companyInitials, monogramTextColor } from '../helpers/company.js';
import Company from '../models/company.js';

import './ns-verification-badge.js';
import { deriveVerificationFactors, aggregateVerificationState } from './verification-factors.js';

export const companyLogo = (company: Company) => company.assets?.logoImageId || company.profileInfo?.logoUrl;

export function companyLogoStyle(company: Company): string {
  const brandColor = company.profileInfo?.brandColor;
  const logo = companyLogo(company);

  const styles: string[] = [];
  if (brandColor) styles.push(`background-color: ${brandColor}`);
  if (logo) styles.push(`background-image: url(${logo})`);
  const initialsColor = !logo && brandColor ? monogramTextColor(brandColor) : undefined;
  if (initialsColor) styles.push(`color: ${initialsColor}`);
  return styles.join('; ');
}

/** 'headline'/'industry' are derived from the company itself; any other string is shown verbatim —
 * lets a consumer with its own second-line text (e.g. a party's role/status in a relationship) use
 * this component without forcing company-derived data into that line. */
export type CompanyCardSubtitle = 'headline' | 'industry' | (string & {});

export function companyCardSubtitle(company: Company, subtitle: CompanyCardSubtitle): string | undefined {
  if (subtitle === 'industry') {
    const industries = (company.industry ?? []).filter((item, index, arr) => arr.indexOf(item) === index);
    return industries.length > 0 ? industries.join(', ') : undefined;
  }
  if (subtitle === 'headline') return company.profileInfo?.shortDescription;

  return subtitle || undefined;
}

@customElement('ns-company-card')
export class NSCompanyCard extends LitElement {
  @property({ attribute: false })
  company?: Company;

  @property({ type: String })
  subtitle: CompanyCardSubtitle = 'headline';

  static styles = [
    css`
      :host {
        display: flex;
        align-items: stretch;

        background: var(--company-card-background, var(--ns-theme-white));
        color: var(--company-card-text-color, var(--text-storm-dust));
        border-radius: calc(var(--wa-border-radius-l) * 2);
        overflow: hidden;
      }

      .company-card-logo {
        flex: none;

        width: 64px;

        display: flex;
        align-items: center;
        justify-content: center;

        background-color: var(--ns-theme-type);
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        color: var(--ns-theme-white);
        font-size: var(--wa-font-size-l);
        font-weight: 700;
      }

      .company-card-info {
        flex: 1;
        min-width: 0;

        padding: var(--wa-space-xs) var(--wa-space-s);

        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .company-card-title {
        font-weight: 500;

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .company-card-headline {
        font-size: var(--wa-font-size-2xs);
        color: var(--company-card-headline-color, var(--ns-theme-type));

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .company-card-verified {
        flex: none;

        display: flex;
        align-items: center;
        justify-content: center;

        padding: 0 var(--wa-space-m);
      }

      /* Scales with the card's own (flex-resolved) height rather than a fixed pixel size, so it
       * stays proportionate whether the card is one line (headline-less) or two (with a subtitle). */
      .company-card-verified-badge {
        height: 55%;
        aspect-ratio: 1 / 1;

        --icon-height: 100%;
        --icon-width: 100%;
      }
    `,
  ];

  render() {
    const company = this.company;
    if (!company) return nothing;

    const subtitle = companyCardSubtitle(company, this.subtitle);
    const factors = deriveVerificationFactors(company);

    return html`
      <div class="company-card-logo" style="${companyLogoStyle(company)}">
        ${companyLogo(company) ? nothing : companyInitials(company.name)}
      </div>
      <div class="company-card-info">
        <div class="company-card-title">${company.name}</div>
        ${subtitle
          ? html`
              <div class="company-card-headline">${subtitle}</div>
            `
          : nothing}
      </div>
      <div class="company-card-verified">
        <ns-verification-badge
          class="company-card-verified-badge"
          .companyId="${company.id}"
          .state="${aggregateVerificationState(factors)}"
          .factors="${factors}"
        ></ns-verification-badge>
      </div>
    `;
  }
}
